/**
 * @author Hiroki Moto
 */
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import queryString from 'query-string';
import { compose, dropLast, find, head, join, juxt, pickBy, propEq, tail, toUpper } from 'ramda';
import Button from '@components/@core/AnimatedButton';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Filter from '@components/@core/Filter';
import Pagination from '@components/@core/Pagination';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Search from '@components/LibarySearch';
import Tab from '@components/@core/Tab';
import CourseCardList from '@components/LibraryCourseCard/List';
import ModuleCardList from '@components/LibraryModuleCard/List';
import TrackCardList from '@components/LibraryTrackCard/List';
import Status from '@components/LibraryStatus';
import AppActions from '~/actions/app';
import DevelopActions from '~/actions/develop';
import { LibraryTrainingTabs } from '~/services/config';
import { isNotNull } from '~/services/util';


const pointFreeUpperCase = compose(dropLast(1), compose(join(''), juxt([compose(toUpper, head), tail])));

const TrainingList = () => {
    // Setup Hooks
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const search = location?.search || '';
    const initialQuery = queryString.parse(search);

    // Redux managed functions
    const getTemplates = useCallback((type, payload) => dispatch(DevelopActions.getlibrarytemplatessearchRequest(type, payload)), []);
    const toggleModal = useCallback((payload) => dispatch(AppActions.modalRequest(payload)), []);

    // Redux state variables
    const userRole = useSelector((state) => state.app.app_role_id);
    const templates = useSelector((state) => state.develop.templates);
    const companyId = useSelector((state) => state.app.company_info.id);
    const employees = useSelector((state) => state.app.users);

    // Local state variables
    const [filter, setFilter] = useState(initialQuery?.filter ?? '');
    const [active, setActive] = useState(initialQuery?.active ?? 'courses');
    const [page, setPage] = useState(Number(initialQuery?.page) || 1);
    const [perPage, setPerPage] = useState(Number(initialQuery?.perPage) || 25);

    const {data, per_page, current_page, total, last_page} = templates[active] || {};
    const templateCount = !data.length ? 0 : total; 

    // Update URL when state changes
    const updateURL = useCallback((newActive, newPage, newPerPage, newFilter) => {
        navigate(`?active=${newActive}&page=${newPage}&perPage=${newPerPage}&filter=${newFilter}`);
    }, [navigate]);

    // Listen for location changes (back/forward navigation)
    useEffect(() => {
        const { active = 'courses', page = 1, perPage = 25, filter = '' } = queryString.parse(location?.search || '');

        setActive(active);
        setPage(Number(page));
        setPerPage(Number(perPage));
        setFilter(filter);
        
        // Fetch data when URL changes
        getTemplates(active, { search: filter, page: Number(page), perPage: Number(perPage) });
    }, [location?.search]);

    // Handler functions
    const handleAdd = useCallback(() => {
        navigate(`/library/training/${active}/new`);
    }, [active]);

    const handleSearch = useCallback((e) => {
        const newFilter = e.target.value;
        setFilter(newFilter);
        setPage(1);
        updateURL(active, 1, perPage, newFilter);
    }, [active, perPage]);

    const handlePage = useCallback((newPage) => {
        setPage(newPage);
        updateURL(active, newPage, perPage, filter);
    }, [active, perPage, filter]);

    const handlePer = useCallback((newPerPage) => {
        setPerPage(newPerPage);
        updateURL(active, page, newPerPage, filter);
    }, [active, page, filter]);

    const handleTab = useCallback((e) => {
        const newActive = e.name;
        setActive(newActive);
        setPage(1);
        updateURL(newActive, 1, perPage, '');
    }, [perPage]);

    const handleDeleteTemplate = useCallback((template) => {
        const target = active === 'tracks' ? 'track' : 'template';
        dispatch(DevelopActions.libraryeventRequest(target, 'delete', template.id, {
            type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST',
            target: active,
            payload: { page, perPage, search: filter },
        }));
    }, [active, page, perPage, filter, dispatch]);

    const handleNavigation = useCallback((id, mode) => {
        const options = queryString.stringify(pickBy(isNotNull, { filter, page, perPage }));

        if (active === 'tracks') {
            const track = find(propEq('id', id), data);
            dispatch(DevelopActions.postlibrarycardtemplatesRequest('tracks', { data: track }, `/library/training/${active}/${id}/${mode}?${options}`));
        } else {
            navigate(`/library/training/${active}/${id}/${mode}?${options}`);
        }
    }, [filter, page, perPage, active, data, dispatch, navigate]);

    const handleSelectMenu = useCallback((event, item) => {
        
        
        switch (event) {
            case 'assign': {
                let payload = {
                    type: 'Assign Training',
                    data: {
                        before: {
                            disabled: active === 'courses' ? ['tracks'] : ['courses'],
                            modules: [item],
                            companyId,
                        },
                    },
                };
                if (active === 'modules') {
                    toast.info('Upcoming Soon.', { position: toast.POSITION.TOP_RIGHT });
                    return;
                }
                toggleModal(payload);
                break;
            }
            case 'detail view': {
                handleNavigation(item.id, 'view');
                break;
            }
            case 'edit': {
                handleNavigation(item.id, 'edit');
                break;
            }
            case 'preview view': {
                if (active === 'tracks') {
                    toast.info('Preview not available for tracks.', { position: toast.POSITION.TOP_RIGHT });
                    return;
                }
                let payload = {
                    type: 'Preview',
                    data: { before: { course: item, module: item.children[0], index: 0 } },
                };
                if (active === 'modules') {
                    if (item.data.presentation_url && !item.data.presentation_images) {
                        toast.warn('This file is being converted to a presentation.  This process takes a few short minutes.', { position: toast.POSITION.TOP_RIGHT });
                        return;
                    }
                    payload = {
                        type: 'Preview',
                        data: { before: { course: { children: [item] }, module: item, index: 0 } },
                    };
                }
                toggleModal(payload);
                break;
            }
            case 'quick assign': {
                toggleModal({
                    type: 'Quick Assign',
                    data: { before: { template: item, type: active, companyId, from: 'template' } },
                });
                break;
            }
            case 'delete':
                toggleModal({
                    type: 'Confirm',
                    data: { before: { title: 'Delete', body: 'Are you sure you want to delete this item?' } },
                    callBack: {
                        onYes: () => handleDeleteTemplate(item),
                    },
                });
                break;
            case 'group training':
                if (item.children.length < 1) {
                    toast.error('Please add modules in the course for using group assign.', {
                        position: toast.POSITION.TOP_RIGHT,
                        pauseOnFocusLoss: false,
                        hideProgressBar: true,
                    });
                } else {
                    const payload = {
                        type: 'Preview',
                        data: {
                            before: { course: item, module: item.children[0], index: 0, attendance: true },
                        },
                    };
                    toggleModal(payload);
                }
                break;
            default:
                break;
        }
    }, [active, companyId, dispatch, handleDeleteTemplate, handleNavigation]);

    const getNameAddButton = useCallback(() => {
        switch (active) {
            case 'modules':
                return 'Add Module';
            case 'courses':
                return 'Add Course';
            default:
                return 'Add Track';
        }
    }, [active]);
    
    return (
        <ErrorBoundary>
            <Filter add={pointFreeUpperCase(active)} onAdd={handleAdd} />
            <div className="card">
                <div className="d-h-between ai-start">
                    <div className="d-flex flex-column w-75">
                        <p className="dsl-b22 bold">Learning Library</p>
                        <ResponsiveTabs active={active} data={LibraryTrainingTabs} getId={(e) => e['name']} getValue={(e) => e['label']} onTab={handleTab} />
                    </div>
                    <Search initial={filter} type={active} onChange={handleSearch} />
                </div>
                <Tab eventKey="tracks" active={active}>
                    <Status type="Tracks" showToggle counts={templateCount} />
                    <TrackCardList userRole={userRole} data={data} onModal={toggleModal} onSelect={handleSelectMenu} onToggle={handleNavigation} />
                </Tab>
                <Tab eventKey="courses" active={active}>
                    <Status type="Courses" showToggle counts={templateCount} />
                    <CourseCardList
                        userRole={userRole}
                        data={data}
                        employees={employees}
                        onModal={toggleModal}
                        onToggle={handleNavigation}
                        onSelect={handleSelectMenu}
                    />
                </Tab>
                <Tab eventKey="modules" active={active}>
                    <Status type="Modules" showToggle counts={templateCount} />
                    <ModuleCardList userRole={userRole} data={data} onSelect={handleSelectMenu} onToggle={handleNavigation} />
                </Tab>
                <Pagination
                    className="pagination"
                    per={Number(per_page)}
                    current={current_page}
                    total={last_page}
                    onChange={handlePage}
                    onPer={handlePer}
                />
                <div className="d-flex mt-2 jc-end">
                    <Button type="high" onClick={handleAdd}>
                        {getNameAddButton()}
                    </Button>
                </div>
            </div>
        </ErrorBoundary>
    );
};

export default TrainingList;